// @ts-check

/**
 * Header Web Components:
 * - <header-scroll> — scroll-aware header with transparent/solid/hidden states
 * - <nav-drawer> — mobile navigation drawer with focus trap
 * - <cart-count> — live cart item count that updates on cart changes
 */
(() => {
  /**
   * Scroll-aware header. Manages three visual states:
   * - transparent: at top, over hero (opt-in via data-transparent)
   * - solid: white background, dark text
   * - hidden: slides up off-screen when scrolling down
   *
   * @attr data-transparent - Enable transparent state at top
   * @attr data-sticky - Enable hide/reveal on scroll
   * @attr data-state - Current visual state (transparent|solid|hidden)
   */
  class HeaderScroll extends HTMLElement {
    connectedCallback() {
      /** @type {boolean} — true when a section on the page signals overlay */
      this.isTransparent = !!document.querySelector('[data-overlay-header]');
      /** @type {boolean} */
      this.isSticky = this.hasAttribute('data-sticky');
      /** @type {number} */
      this.lastScrollY = window.scrollY;
      /** @type {number} */
      this.threshold = 72;

      // Set --header-height for other components
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.offsetHeight}px`
      );

      if (this.isTransparent) {
        this._handleScroll();
      }

      if (this.isSticky || this.isTransparent) {
        this._onScroll = this._handleScroll.bind(this);
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
      }
    }

    _handleScroll() {
      const scrollY = window.scrollY;
      const pastThreshold = scrollY > this.threshold;

      if (!pastThreshold && this.isTransparent) {
        this.setAttribute('data-state', 'transparent');
      } else {
        this.setAttribute('data-state', 'solid');
      }
    }
  }

  /**
   * Mobile navigation drawer. Opens/closes with animation,
   * traps focus, locks body scroll.
   *
   * Trigger: any button with aria-controls matching this element's ID.
   * Close: any child with [data-close] attribute, or Escape key.
   */
  class NavDrawer extends HTMLElement {
    connectedCallback() {
      this._trigger = document.querySelector(
        `[aria-controls="${this.id}"]`
      );

      // Close buttons (overlay + X button)
      this.querySelectorAll('[data-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      // Open trigger
      if (this._trigger) {
        this._trigger.addEventListener('click', () => this.open());
      }

      // Escape key
      this._onKeydown = (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
      }
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      if (this._trigger) {
        this._trigger.setAttribute('aria-expanded', 'true');
      }

      // Focus first link after a frame (let transition start)
      requestAnimationFrame(() => {
        const firstFocusable = this.querySelector(
          'a[href], button:not([disabled]), [tabindex="0"]'
        );
        if (firstFocusable) {
          /** @type {HTMLElement} */ (firstFocusable).focus();
        }
      });
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (this._trigger) {
        this._trigger.setAttribute('aria-expanded', 'false');
        /** @type {HTMLElement} */ (this._trigger).focus();
      }
    }
  }

  /**
   * Live cart item count. Updates when cart changes via
   * Section Rendering API events.
   *
   * Listens to 'cart:change' custom event on document.
   */
  class CartCount extends HTMLElement {
    connectedCallback() {
      this._onCartChange = (/** @type {CustomEvent} */ e) => {
        const count = e.detail?.count;
        if (count != null) {
          this._update(count);
        }
      };
      document.addEventListener('cart:change', this._onCartChange);

      this._onCartChangeSilent = (/** @type {CustomEvent} */ e) => {
        const count = e.detail?.count;
        if (count != null) this._update(count);
      };
      document.addEventListener('cart:change:silent', this._onCartChangeSilent);
    }

    disconnectedCallback() {
      if (this._onCartChange) {
        document.removeEventListener('cart:change', this._onCartChange);
      }
      if (this._onCartChangeSilent) {
        document.removeEventListener('cart:change:silent', this._onCartChangeSilent);
      }
    }

    /** @param {number} count */
    _update(count) {
      const span = this.querySelector('[aria-hidden]');
      const srSpan = this.querySelector('.visually-hidden');
      if (span) span.textContent = String(count);
      if (srSpan) srSpan.textContent = `${count} items in cart`;

      const badge = this.querySelector('[aria-hidden]');
      if (badge) badge.style.display = count === 0 ? 'none' : '';
    }
  }

  // Bag button dispatches cart:open for the cart drawer
  document.addEventListener('DOMContentLoaded', () => {
    const bagButton = document.querySelector('[aria-controls="cart-drawer"]');
    if (bagButton) {
      bagButton.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
    }
  });

  // Search drawer trigger
  document.querySelectorAll('[data-search-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('search:open'));
    });
  });

  customElements.define('header-scroll', HeaderScroll);
  customElements.define('nav-drawer', NavDrawer);
  customElements.define('cart-count', CartCount);
})();
